import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';

// Import chart components from TL Dashboard
import MostUsedMachinesChart from '../charts/MostUsedMachinesChart';
import MachineStatusOverviewChart from '../charts/MachineStatusOverviewChart';
import MostIssuedItemsChart from '../charts/MostIssuedItemsChart';
import CostVsUsageChart from '../charts/CostVsUsageChart';

// Import MBF-specific components
import MBFMachinesOutOfOrderTable from './charts/MBFMachinesOutOfOrderTable';

// Import common components and CSS
import { FullscreenChartModal, ErrorDisplay, SampleDataBadge } from '../common';
import { MaximizeIcon } from '../../../Icons/DashboardIcons';
import '../css/MachineUsage.css';
import '../css/MachineStatusOverview.css';
import '../css/MostIssuedItems.css';
import '../css/CostVsUsage.css';

// Import API
import MBFDashboardAPIs from '../../../utilities/api/dashboard/MBFDashboardAPIs';

// Import sample data utilities
import { isMBFDataEmpty, getMBFSampleData } from '../../../utilities/sampleData/mbfDashboardSampleData';

/**
 * MBF Machine Usage Tab Component
 * Displays machine usage charts with API integration
 * 
 * @param {object} dateRange - Date range filter { startDate, endDate }
 * @param {string} selectedTL - Selected TL ID for filtering
 */
const MBFMachineUsageTab = ({ dateRange = {}, selectedTL = '' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [maximizedChart, setMaximizedChart] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [oOOPeriod, setOOOPeriod] = useState('this_month');

    // Fetch data when dateRange, selectedTL, or refreshTrigger changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await MBFDashboardAPIs.getMachineUsage({
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    tlId: selectedTL,
                    oOOPeriod: oOOPeriod,
                });
                setData(response);
            } catch (err) {
                console.error('Error fetching MBF machine usage data:', err);
                setError('Failed to load machine usage data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange.startDate, dateRange.endDate, selectedTL, refreshTrigger, oOOPeriod]);

    const handleOOOPeriodChange = (period) => {
        setOOOPeriod(period);
    };

    const handleRetry = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={48} sx={{ color: '#007bff' }} />
                <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    Fetching machine usage data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} onRetry={handleRetry} />;
    }

    // Extract data from API response with sample data fallbacks
    const rawMostUsed = data?.most_used_machines || [];
    const isMostUsedEmpty = isMBFDataEmpty(rawMostUsed, 'array');
    const mostUsedMachines = isMostUsedEmpty ? getMBFSampleData('mostUsedMachines') : rawMostUsed;

    const rawMachineStatus = data?.machine_status_overview || {
        operational: 0,
        under_maintenance: 0,
        out_of_order: 0,
        total_machines: 0
    };
    const isMachineStatusEmpty = rawMachineStatus.operational === 0 && rawMachineStatus.under_maintenance === 0 && rawMachineStatus.out_of_order === 0;
    const machineStatusOverview = isMachineStatusEmpty ? getMBFSampleData('machineStatusOverview') : rawMachineStatus;

    const rawMostIssued = data?.most_issued_items || [];
    const isMostIssuedEmpty = isMBFDataEmpty(rawMostIssued, 'array');
    const mostIssuedItems = isMostIssuedEmpty ? getMBFSampleData('mostIssuedItems') : rawMostIssued;

    const rawCostVsUsage = data?.cost_vs_usage || [];
    const isCostVsUsageEmpty = isMBFDataEmpty(rawCostVsUsage, 'array');
    const costVsUsage = isCostVsUsageEmpty ? getMBFSampleData('costVsUsage') : rawCostVsUsage;

    const rawMachinesOutOfOrder = data?.machines_out_of_order || [];
    const isMachinesOutOfOrderEmpty = rawMachinesOutOfOrder.length === 0;
    const machinesOutOfOrder = isMachinesOutOfOrderEmpty ? getMBFSampleData('machinesOutOfOrder') : rawMachinesOutOfOrder;

    // Get period label for legend (default to monthly for MBF)
    const periodLabel = 'Monthly';
    const selectedPeriod = 'monthly';

    // Helper function to get chart title
    const getChartTitle = (chartType) => {
        switch (chartType) {
            case 'mostUsedMachines': return 'Most Used Machines';
            case 'machineStatus': return 'Machine Status Overview';
            case 'mostIssuedItems': return 'Most Issued Items';
            case 'costVsUsage': return 'Cost of Machine vs Usage Hours';
            case 'machinesOutOfOrder': return 'Machines Out of Order';
            default: return '';
        }
    };

    // Helper function to render chart content in fullscreen
    const renderChartContent = (chartType) => {
        switch (chartType) {
            case 'mostUsedMachines':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MostUsedMachinesChart data={mostUsedMachines} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'machineStatus':
                return (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MachineStatusOverviewChart data={machineStatusOverview} isFullscreen={true} />
                    </Box>
                );
            case 'mostIssuedItems':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MostIssuedItemsChart data={mostIssuedItems} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'costVsUsage':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <CostVsUsageChart data={costVsUsage} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'machinesOutOfOrder':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFMachinesOutOfOrderTable
                                data={machinesOutOfOrder}
                                isSampleData={isMachinesOutOfOrderEmpty}
                                period={oOOPeriod}
                                onPeriodChange={handleOOOPeriodChange}
                                onRefresh={handleRetry}
                                isFullscreen={true}
                            />
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', py: 2 }}>
            {/* Fullscreen Chart Modal */}
            <FullscreenChartModal
                open={Boolean(maximizedChart)}
                onClose={() => setMaximizedChart(null)}
                title={getChartTitle(maximizedChart)}
            >
                {renderChartContent(maximizedChart)}
            </FullscreenChartModal>

            {/* First Row - Most Used Machines and Machine Status Overview */}
            <Grid container spacing={3}>
                {/* Most Used Machines Chart */}
                <Grid item xs={12} md={6}>
                    <Box className="most-used-machines" sx={{ height: '450px' }}>
                        <Box className="header">
                            <Box className="frame-1000001142">
                                <Typography className="title">Most Used Machines</Typography>
                                {isMostUsedEmpty && <SampleDataBadge absolute={false} />}
                            </Box>
                            <Box className="controls">
                                <Box
                                    className="drop-down"
                                    onClick={() => setMaximizedChart('mostUsedMachines')}
                                    sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                                >
                                    <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MaximizeIcon color="#333" />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box className="content">
                            <Box className="content2" style={{ position: 'relative' }}>
                                <MostUsedMachinesChart data={mostUsedMachines} />
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Machine Status Overview Chart */}
                <Grid item xs={12} md={6}>
                    <Box className="machine-status-overview" sx={{ height: '450px' }}>
                        <Box className="header">
                            <Box className="frame-1000001142">
                                <Typography className="title">Machine Status Overview</Typography>
                                {isMachineStatusEmpty && <SampleDataBadge absolute={false} />}
                            </Box>
                            <Box className="controls">
                                <Box
                                    className="drop-down"
                                    onClick={() => setMaximizedChart('machineStatus')}
                                    sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                                >
                                    <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MaximizeIcon color="#333" />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box className="content">
                            <Box className="chart">
                                <MachineStatusOverviewChart data={machineStatusOverview} />
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Second Row - Most Issued Items and Cost vs Usage Hours */}
            <Grid container spacing={3}>
                {/* Most Issued Items Chart */}
                <Grid item xs={12} md={6}>
                    <Box className="most-issued-items" sx={{ height: '450px' }}>
                        <Box className="header">
                            <Box className="frame-1000001142">
                                <Typography className="title">Most Issued Items</Typography>
                                {isMostIssuedEmpty && <SampleDataBadge absolute={false} />}
                            </Box>
                            <Box className="controls">
                                <Box
                                    className="drop-down"
                                    onClick={() => setMaximizedChart('mostIssuedItems')}
                                    sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                                >
                                    <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MaximizeIcon color="#333" />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box className="content">
                            <Box className="content2" style={{ position: 'relative' }}>
                                <MostIssuedItemsChart data={mostIssuedItems} />
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Cost vs Usage Hours Chart */}
                <Grid item xs={12} md={6}>
                    <Box className="cost-of-machine-vs-usage-hours" sx={{ height: '450px' }}>
                        <Box className="header">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography className="title">Cost of Machine vs Usage Hours</Typography>
                                {isCostVsUsageEmpty && <SampleDataBadge absolute={false} />}
                            </Box>
                            <Box className="controls">
                                <Box
                                    className="drop-down"
                                    onClick={() => setMaximizedChart('costVsUsage')}
                                    sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                                >
                                    <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MaximizeIcon color="#333" />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box className="content">
                            <Box className="content2">
                                <CostVsUsageChart data={costVsUsage} />
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Third Row - Machines Out of Order Table */}
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <MBFMachinesOutOfOrderTable
                        data={machinesOutOfOrder}
                        isSampleData={isMachinesOutOfOrderEmpty}
                        period={oOOPeriod}
                        onPeriodChange={handleOOOPeriodChange}
                        onRefresh={handleRetry}
                        onMaximize={() => setMaximizedChart('machinesOutOfOrder')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default MBFMachineUsageTab;
